// objectives-ui.ts — the in-game OBJECTIVES BRIEFING overlay (domShowObjectives), the DOM form
// the session raises on ksoObjectives (and the HUD credits-tap → openObjectivesForAutomation).
// Lifted verbatim from main.ts's mountObjectives, a sibling of command-ui.ts / hud-seams.ts. The
// mission editor's Play host uses it now; main.ts's inline mountObjectives migrates onto it next
// (the same extract-&-share path as the command UI + HUD seams), so they converge with no drift.

import { ObjectivesView } from './objectives-view.ts';
import type { ObjectivesSnapshot } from './objectives-snapshot.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  type ButtonArt,
} from '../title-art.ts';
import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';

export interface ObjectivesUiDeps {
  /** the texture atlas mgr — sources the title-screen backdrop (titlescreenbkgd.png) + the
   *  9-slice button frame (extractButtonArt). */
  tam: TexAtlasMgr;
  /** optional pre-extracted 9-slice button art; else extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** play a GUI sfx on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
}

export interface ObjectivesUi {
  /** the domShowObjectives session opt (gameSessionOpts.domShowObjectives) — the session builds
   *  an ObjectivesSnapshot + calls this to raise the briefing; `done()` resumes the paused game. */
  domShowObjectives: (snapshot: ObjectivesSnapshot, done: () => void) => void;
  /** idempotently unmount the briefing if it's up (game-over / host close). */
  teardown(): void;
}

/** Stand up the shared objectives-briefing overlay for one play session. */
export function createObjectivesUI(deps: ObjectivesUiDeps): ObjectivesUi {
  const { tam, playSfx } = deps;
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;
  const objBgArt = extractAtlasArt(tam, 'titlescreenbkgd.png');
  const titleBg = objBgArt !== null ? titleArtToDataUrl(objBgArt) : undefined;

  const root = document.createElement('div');
  root.id = 'dom-objectives';
  root.style.display = 'none';
  (deps.host ?? document.body).appendChild(root);
  let view: ObjectivesView | null = null;

  const unmount = (): void => {
    root.style.display = 'none';
    view?.destroy();
    view = null;
    root.replaceChildren();
  };

  const domShowObjectives = (snapshot: ObjectivesSnapshot, done: () => void): void => {
    root.style.display = 'flex';
    view = new ObjectivesView({
      snapshot,
      root,
      // OK / backdrop / Esc → unmount FIRST, then resume the game (the caller's SP pause releases).
      onClose: () => {
        unmount();
        done();
      },
      titleBg,
      buttonArt: objBtnArt,
      playSfx: (sfx: number) => {
        playSfx(sfx);
      },
    });
  };

  return { domShowObjectives, teardown: unmount };
}
